import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// ─────────────────────────────────────────
// useServiceCatalogOverrides
//
// Loads optional per-slug overrides for the short structural fields
// (name / subtitle / description / planNote) from
// platform_settings.service_catalog_overrides. Allows marketing to
// tweak copy without redeploying.
//
// Shape:
//   Record<slug, { name?, subtitle?, description?, planNote? }>
//
// Long-form fields (benefits / forWho / faqs / previewText) are NOT
// overridden here, those are regenerated by the AI function with
// per-language support.
// ─────────────────────────────────────────

export interface ServiceCatalogOverride {
  name?:        string;
  subtitle?:    string;
  description?: string;
  planNote?:    string;
}

export type ServiceCatalogOverrides = Record<string, ServiceCatalogOverride>;

let cache: { data: ServiceCatalogOverrides; at: number } | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

export async function loadServiceCatalogOverrides(force = false): Promise<ServiceCatalogOverrides> {
  if (!force && cache && Date.now() - cache.at < CACHE_TTL_MS) return cache.data;

  const { data } = await supabase
    .from('platform_settings')
    .select('value')
    .eq('key', 'service_catalog_overrides')
    .maybeSingle();

  if (!data?.value) {
    cache = { data: {}, at: Date.now() };
    return {};
  }

  try {
    const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
    const overrides = (parsed && typeof parsed === 'object' ? parsed : {}) as ServiceCatalogOverrides;
    cache = { data: overrides, at: Date.now() };
    return overrides;
  } catch {
    return {};
  }
}

export function clearServiceCatalogOverridesCache() {
  cache = null;
}

export function useServiceCatalogOverride(slug: string | undefined): ServiceCatalogOverride | null {
  const [override, setOverride] = useState<ServiceCatalogOverride | null>(null);

  useEffect(() => {
    if (!slug) {
      setOverride(null);
      return;
    }
    let active = true;
    loadServiceCatalogOverrides().then(all => {
      if (!active) return;
      setOverride(all[slug] ?? null);
    });
    return () => { active = false; };
  }, [slug]);

  return override;
}
